import { getAdjacentTiles, getRandomTile } from "@/store/utils";
import {NS_GAME_STATE, NS_LAYER_STATE} from "@/store/constants";
import {
  EDGE_OF_BOARD,
  HOTELS
} from "@/store/game/constants";
import { cloneDeep, orderBy } from 'lodash';
import {
  createMergerHotel,
  createMergerState,
} from "@/store/game/schema";

// Construct an object that lets us control the logic for a placed tile
function createPlaceTileLogicController(board, adjacentTiles) {
  const logicControl = {
    hotels: {},
    empty: [],
  };

  return Object.values(adjacentTiles).reduce((obj, tileId) => {
    if(tileId === EDGE_OF_BOARD) {
      obj['empty'].push(EDGE_OF_BOARD);
      return obj;
    }

    const tile = board[tileId];
    if (!tile) {
      throw new Error('TileID not found on board');
    }

    if (HOTELS.includes(tile.hotel)) {
      obj['hotels'][tile.hotel] = tile.hotel;
      return obj;
    }

    if (tile.isEmpty) {
      obj['empty'].push(tile);
      return obj;
    }

    if (!tile.isEmpty) {
      return obj;
    }
  }, logicControl);
}

// function isMyTurn() {
//   // TODO: add this check to every function in the turn store. You can't take these actions unless it is your turn.
// }

export default {
  async flowPlaceTile({ dispatch, rootGetters }, tileId) {
    const isMyTurn = rootGetters[`${NS_GAME_STATE}/isMyTurn`];
    if (!isMyTurn) {
      dispatch(`${NS_LAYER_STATE}/addToast`, { message: 'You can only place tiles on your turn' }, {root: true});
      return;
    }

    const hasPlacedTile = rootGetters[`${NS_GAME_STATE}/hasPlacedTile`];
    if (hasPlacedTile) {
      dispatch(`${NS_LAYER_STATE}/addToast`, { message: 'You can only place 1 tile per turn' }, {root: true});
      return;
    }

    dispatch(`${NS_GAME_STATE}/setSelectedTile`, tileId, { root: true });

    const board = rootGetters[`${NS_GAME_STATE}/board`];
    const adjacentTiles = getAdjacentTiles(tileId);
    const tile = cloneDeep(board[tileId]);
    const logicControl = createPlaceTileLogicController(board, adjacentTiles);
    console.log('logicControl', logicControl);

    // If all surrounding tiles are empty, just place the tile
    const allAdjacentTilesEmpty = logicControl.empty.length === 4;
    if (allAdjacentTilesEmpty) {
      tile.isEmpty = false;
      dispatch('placeTile', tile);
      return;
    }

    const hotels = Object.values(logicControl.hotels);
    const hotelCount = hotels.length;

    // If no hotels, but an adjacent tile has been placed, start a company
    if(hotelCount === 0) {
      tile.isEmpty = false;
      dispatch('placeTile', tile);
      dispatch('flowPickNewCompany');
      return;
    }

    // If only one hotel, add to the chain
    if (hotelCount === 1) {
      await dispatch('updateTilesRecursively', {
        startingTileId: tileId,
        hotelId: hotels[0]
      });
      return;
    }

    // If more than one hotel, there is a merger that needs to happen
    if (hotelCount > 1) {
      // place the tile so it shows up on the board
      tile.isEmpty = false;
      dispatch('placeTile', tile);
      dispatch('flowStartMerger', { hotels });
    }
  },

  flowPickNewCompany({ dispatch, rootGetters }) {
    const availableHotels = rootGetters[`${NS_GAME_STATE}/hotelsAvailableForNewCompany`];
    if (availableHotels.length <= 0) {
      dispatch(`${NS_LAYER_STATE}/addToast`, { message: 'All companies have already been started. Skipping to the next part of your turn.' }, { root: true });
      return;
    }

    rootGetters[`${NS_GAME_STATE}/gameRef`].update({
      'newCompany': true,
    });
  },

  async flowCompletePlacingNewCompany({ dispatch, rootGetters }, hotelId) {
    const currentTile = rootGetters[`${NS_GAME_STATE}/selectedTile`];

    // Update the current tile with the hotel the user chose
    // await rootGetters[`${NS_GAME_STATE}/gameRef`].update({
    //   [`board/${currentTile}/hotel`]: hotelId,
    //   [`board/${currentTile}/isEmpty`]: false,
    // });

    await dispatch(`${NS_GAME_STATE}/giveBonusStock`, hotelId, { root: true });

    // TODO: show modal that tells them they got 1 stock for free for placing the company
    // TODO: show a modal to all other places that XYZ player just received 1 stock and place a new company

    await dispatch('updateTilesRecursively', {
      handleParentTile: true,
      startingTileId: currentTile,
      hotelId
    });


    await rootGetters[`${NS_GAME_STATE}/gameRef`].update({
      'newCompany': false,
    });
  },

  async flowStartMerger({ dispatch, rootGetters }, { hotels }) {
    // TODO: find all players that currently hold stocks in each company that is going to be merged.
    const players = rootGetters[`${NS_GAME_STATE}/players`];

    let tilesPerHotel = hotels.map(hotelId => {
      const count = rootGetters[`${NS_GAME_STATE}/tilesPerHotel`][hotelId].length;
      // const localPlayerId = rootGetters[`${NS_GAME_STATE}/localPlayer`].id;
      const playersWithStocks = Object.values(players).filter(player => {
        return players[player.id].stocks[hotelId] > 0;
        // const isMyTurn = player.id === localPlayerId;
      });
      const playerOrderReindexed = rootGetters[`${NS_GAME_STATE}/playerOrderReindexed`];
      const playerIds = playersWithStocks.map(player => player.id);
      const orderedPlayerId = orderBy(playerIds, (playerId) => playerOrderReindexed.indexOf(playerId));

      return createMergerHotel({
        count,
        hotelId,
        nextTurnPlayerIds: orderedPlayerId,
      })
    });

    // TODO: if any 2 hotels have a size >= 11, this merger is not possible
      // TODO: automatically remove tiles that would cause an unmergeable merger

    tilesPerHotel = orderBy(tilesPerHotel, ['count'], ['desc']);

    // For simplicity, always pick the first company to always be surviving company.
    // TODO: Should allow the user to pick surviving company and merge order if there is a tie
    const survivingHotel = tilesPerHotel[0];
    const mergeOrder = tilesPerHotel.filter(item => {
      if (item.hotelId === survivingHotel.hotelId) {
        return false;
      }
      return true;
    });

    const mergerState = createMergerState({
      hotelsToBeMerged: mergeOrder,
      mergingHotelIndex: 0, //index of the `hotelsToBeMerged`
      survivingHotelId: survivingHotel.hotelId,
      mergingPlayerId: rootGetters[`${NS_GAME_STATE}/localPlayerId`],
    });

    rootGetters[`${NS_GAME_STATE}/gameRef`].update({
      'merger': mergerState
    });

    dispatch('handleBonuses', { hotelId: mergeOrder[0].hotelId });

    // The larger chain takes over all smaller chains simultaneously. The multiple merger is then handled as individual mergers.
    //    Majority holder’s bonuses are paid for the larger of defunct chains and players dispose of their stock in this chain.
    //    Then the smaller defunct chain is handled in the same manner.
    //    In the case of a tie, the merger maker chooses which defunct chain is to be handled first.
    //    Bonuses and stock prices are determined by the number of hotels in the defunct chain before the merger.
  },

  async flowMergerPlayerDone({ dispatch, rootGetters }, { playerId, fromHotelId, toHotelId, sold, kept, traded }) {
    await dispatch('updateAfterMergingPlayersTurn', { playerId, fromHotelId, toHotelId, kept, sold, traded });
    debugger;
    const mergingNextTurnPlayerIds = rootGetters[`${NS_GAME_STATE}/mergingNextTurnPlayerIds`];
    if (mergingNextTurnPlayerIds.length > 1) {
      debugger;
      await dispatch('setNextMergingPlayer');
      return;
    }

    // No players left for this hotel, remove it from the board
    dispatch('flowMergerRemoveHotel', { hotelId: fromHotelId });

    // See if another hotel exists that needs to be merged
    const mergerDetails = rootGetters[`${NS_GAME_STATE}/mergerDetails`];
    const nextHotelIndex = mergerDetails.mergingHotelIndex + 1;
    const nextHotel = mergerDetails.hotelsToBeMerged[nextHotelIndex];
    if (nextHotel && nextHotelIndex >= 0) {
      debugger;
      rootGetters[`${NS_GAME_STATE}/gameRef`].update({
        [`merger/mergingHotelIndex`]: nextHotelIndex,
      });
      dispatch('handleBonuses', { hotelId: nextHotel.hotelId });
      return;
    }

    debugger;
    await dispatch('flowMergerEnd');
  },

  async flowMergerRemoveHotel({ dispatch, rootGetters }, { hotelId }) {
    debugger;
    const startingTileId = rootGetters[`${NS_GAME_STATE}/selectedTile`];
    const survivingHotelId = rootGetters[`${NS_GAME_STATE}/survivingHotelId`];

    await dispatch('updateTilesRecursively', {
      hotelIdToRemove: hotelId,
      startingTileId,
      hotelId: survivingHotelId,
    });
  },

  async flowMergerEnd({rootGetters }) {
    debugger;
    await rootGetters[`${NS_GAME_STATE}/gameRef`].update({
      [`merger`]: false,
    });
  },

  setNextMergingPlayer({ rootGetters }) {
    const mergingHotelIndex = rootGetters[`${NS_GAME_STATE}/mergerDetails`].mergingHotelIndex;
    const nextTurnPlayerIds = cloneDeep(rootGetters[`${NS_GAME_STATE}/mergingNextTurnPlayerIds`]);
    nextTurnPlayerIds.shift();

    rootGetters[`${NS_GAME_STATE}/gameRef`].update({
      [`merger/hotelsToBeMerged/${mergingHotelIndex}/nextTurnPlayerIds`]: nextTurnPlayerIds,
    });
  },

  updateAfterMergingPlayersTurn({ rootGetters }, { playerId, fromHotelId, toHotelId, kept, sold, traded }) {
    const players = rootGetters[`${NS_GAME_STATE}/players`];
    const pricePerStock = rootGetters[`${NS_GAME_STATE}/stockMoneyPerHotel`][fromHotelId].price;
    const originalToHotelCount = rootGetters[`${NS_GAME_STATE}/stocks`][toHotelId].quantity;
    const originalFromHotelCount = rootGetters[`${NS_GAME_STATE}/stocks`][fromHotelId].quantity;
    const toHotelTradeValue = (traded / 2);

    const updateData = {
      [`players/${playerId}/stocks/${fromHotelId}`]: kept,
      [`players/${playerId}/stocks/${toHotelId}`]: players[playerId].stocks[toHotelId] + toHotelTradeValue,
      [`players/${playerId}/money`]: players[playerId].money + (sold * pricePerStock),
      [`stocks/${fromHotelId}/quantity`]: originalFromHotelCount + sold + traded,
      [`stocks/${toHotelId}/quantity`]: originalToHotelCount - toHotelTradeValue,
    };

    rootGetters[`${NS_GAME_STATE}/gameRef`].update(updateData);
  },

  placeTile({ rootGetters }, tile) {
    if (!tile) {
      throw new Error('tile required');
    }
    rootGetters[`${NS_GAME_STATE}/gameRef`].child(`board/${tile.id}`)
      .update(tile);
  },

  /**
   * Find tiles that have been placed, but do not have a hotel associated
   */
  async updateTilesRecursively({ dispatch }, { startingTileId, hotelId, handleParentTile = false, hotelIdToRemove = false }) {
    await dispatch(`${NS_GAME_STATE}/updateChildTiles`, {
      handleParentTile,
      hotelIdToRemove,
      startingTileId,
      newTile: {
        hotel: hotelId,
        id: startingTileId,
        isChild: true,
        isEmpty: false,
      },
    }, { root: true });
  },

  async handleBonuses({ dispatch, rootGetters }, { hotelId }) {
    const majorityHolders = rootGetters[`${NS_GAME_STATE}/majorityHoldersPerHotel`][hotelId];
    const bonusAmounts = rootGetters[`${NS_GAME_STATE}/stockMoneyPerHotel`][hotelId];
    const firstCount = majorityHolders.first.length;
    const secondCount = majorityHolders.second.length;

    // No one owns stock, no bonus given
    if (firstCount === 0 && secondCount === 0) {
      return;
    }

    // Split 1st/2nd bonus between all first place players
    // In the case of a tie for the title of largest shareholder, the first and second bonuses are combined and divided equally between the tying shareholders.
    if (firstCount > 1) {
      const amountPerPlayer = (bonusAmounts.firstBonus + bonusAmounts.secondBonus) / firstCount;
      majorityHolders.first.forEach(async (playerId) => {
        dispatch('addMoneyToPlayer', { playerId, amount: amountPerPlayer });
      });
      return;
    }

    // Give first bonus to the first place player
    if (firstCount === 1) {
      dispatch('addMoneyToPlayer', {
        playerId: majorityHolders.first[0],
        amount: bonusAmounts.firstBonus
      });
    }

    // Split 2nd bonus between all second place players
    // In the case of a tie for the title of second largest shareholder, the second bonus is divided equally between the tying players.
    if (secondCount > 1) {
      const amountPerPlayer = bonusAmounts.secondBonus / secondCount;
      majorityHolders.second.forEach(async (playerId) => {
        dispatch('addMoneyToPlayer', {
          playerId,
          amount: amountPerPlayer
        });
      });
      return;
    }

    // Only 1 second place player, give bonus to them
    if (secondCount === 1) {
      dispatch('addMoneyToPlayer', {
        playerId: majorityHolders.second[0],
        amount: bonusAmounts.secondBonus
      });
      return;
    }

    // If there was a first place but no second place, give the second place bonus to the first place player
    if (secondCount === 0) {
      dispatch('addMoneyToPlayer', {
        playerId: majorityHolders.first[0],
        amount: bonusAmounts.secondBonus
      });
    }
  },

  async flowEndTurn({ dispatch, rootGetters }) {
    // TODO: only show your money/balance if it's your turn.
    // TODO: show the total count of stocks the current user has in the All players section
    const localPlayer = rootGetters[`${NS_GAME_STATE}/localPlayer`];

    // Find the next player
    const nextPlayerId = rootGetters[`${NS_GAME_STATE}/nextPlayerId`];
    const localPlayerId = rootGetters[`${NS_GAME_STATE}/localPlayerId`];

    // Update current players money
    const totalPrice = rootGetters[`${NS_GAME_STATE}/totalPrice`];
    const currentPlayerMoney = localPlayer.money;
    const balance = currentPlayerMoney - totalPrice;

    // Update current players stock quantity
    let playersHotels = cloneDeep(rootGetters[`${NS_GAME_STATE}/localPlayerStocks`]);
    const stocksToBePurchased = rootGetters[`${NS_GAME_STATE}/stocksToBePurchased`];
    stocksToBePurchased.forEach(stock => {
      playersHotels[stock] = playersHotels[stock] + 1;
    });

    // Remove the users tile and give them a new one
    const tileData = getRandomTile(rootGetters[`${NS_GAME_STATE}/tiles`]);
    const playerTiles = cloneDeep(localPlayer.tiles);
    const selectedTile = rootGetters[`${NS_GAME_STATE}/selectedTile`];
    const selectedTileIndex = playerTiles.indexOf(selectedTile);
    playerTiles.splice(selectedTileIndex, 1, tileData.tile);

    const resetData = {
      merger: false,
      tiles: tileData.tiles,
      newCompany: false,
      selectedTile: false,
      whosTurn: nextPlayerId,
      stocksToBePurchased: null,
      [`players/${localPlayerId}/money`]: balance,
      [`players/${localPlayerId}/tiles`]: playerTiles,
      [`players/${localPlayerId}/stocks`]: playersHotels,
    };

    await dispatch(`${NS_GAME_STATE}/updateMultipleProperties`, resetData, { root: true });
  },

  async addMoneyToPlayer({ rootGetters }, { playerId, amount }) {
    const startingMoney = rootGetters[`${NS_GAME_STATE}/players`][playerId].money;
    if (!startingMoney && startingMoney !== 0) {
      throw new Error('The player has undefined money, thats not right!');
    }

    const newMoney = startingMoney + amount;
    if (!newMoney || newMoney < 0) {
      throw new Error('newMoney not defined, what up with dat');
    }

    rootGetters[`${NS_GAME_STATE}/gameRef`].child(`players/${playerId}`).update({
      'money': newMoney
    });
  }
}
